/* Native disclosures, no dependency and no network request until an image is shown. */
if (!customElements.get('tbc-navigation')) {
  customElements.define('tbc-navigation', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const desktop = this.dataset.mode === 'desktop';
      const details = [...this.querySelectorAll('.tbc-nav__details')];
      const close = (item, focus = false) => {
        delete item.dataset.hoverOpened;
        item.open = false;
        const summary = item.querySelector('summary');
        summary.setAttribute('aria-expanded', 'false');
        if (focus) summary.focus();
      };
      this.closeAll = () => details.forEach(item => close(item));
      const prepare = item => {
        const panel = item.querySelector('.tbc-nav__panel');
        this.style.setProperty('--tbc-panel-top', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
        panel.querySelectorAll('template[data-nav-image]').forEach(template => {
          template.replaceWith(template.content.cloneNode(true));
        });
      };
      details.forEach(item => {
        item.addEventListener('toggle', () => {
          item.querySelector('summary').setAttribute('aria-expanded', String(item.open));
          if (item.open) {
            details.filter(other => other !== item && other.open).forEach(other => close(other));
            prepare(item);
          }
        }, options);
        // A hover may open the panel just before the click lands: keep it open instead of toggling it shut.
        item.querySelector('summary').addEventListener('click', event => {
          if (desktop && item.open && item.dataset.hoverOpened) event.preventDefault();
          delete item.dataset.hoverOpened;
        }, options);
        item.querySelector('summary').addEventListener('keydown', event => {
          if (desktop && event.key === 'ArrowDown') {
            event.preventDefault();
            item.open = true;
            prepare(item);
            item.querySelector('.tbc-nav__panel a')?.focus();
          }
        }, options);
        if (desktop) {
          item.addEventListener('pointerenter', event => {
            if (event.pointerType !== 'mouse') return;
            clearTimeout(this.leaveTimer);
            clearTimeout(this.enterTimer);
            this.enterTimer = setTimeout(() => {
              if (item.open) return;
              item.dataset.hoverOpened = '1';
              item.open = true;
            }, 180);
          }, options);
          item.addEventListener('pointerleave', () => {
            clearTimeout(this.enterTimer);
            this.leaveTimer = setTimeout(() => {
              if (!item.contains(document.activeElement)) close(item);
            }, 260);
          }, options);
        }
      });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.getClientRects().length) return;
        const opened = details.find(item => item.open);
        if (!opened) return;
        // Leave the mobile drawer open on the first Escape, close it on the next.
        if (desktop || this.contains(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.consumedEscape = true;
          clearTimeout(this.enterTimer);
          close(opened, true);
        }
      }, { ...options, capture: true });
      document.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.consumedEscape) {
          this.consumedEscape = false;
          event.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      if (desktop) {
        document.addEventListener('pointerdown', event => {
          if (!this.contains(event.target)) this.closeAll();
        }, options);
        // During a press on a panel link, focus briefly leaves the summary for the body:
        // closing then would hide the link before the click lands.
        this.addEventListener('pointerdown', () => { this.pressing = true; }, options);
        document.addEventListener('pointerup', () => setTimeout(() => { this.pressing = false; }), options);
        this.addEventListener('focusout', () => {
          queueMicrotask(() => {
            if (!this.pressing && !this.contains(document.activeElement)) this.closeAll();
          });
        }, options);
      } else {
        const drawer = this.closest('#NavDrawer');
        drawer?.querySelector('.js-drawer-close')?.addEventListener('click', () => this.closeAll(), options);
        document.addEventListener('drawerOpen.NavDrawer', () => this.closeAll(), options);
        document.addEventListener('keydown', event => {
          if (event.key !== 'Tab' || !drawer?.classList.contains('drawer--is-open')) return;
          const focusable = [...drawer.querySelectorAll('button, a[href], input:not([type="hidden"]), summary')]
            .filter(node => node.getClientRects().length && !node.disabled);
          const first = focusable[0], last = focusable.at(-1);
          const active = document.activeElement;
          if (event.shiftKey && (active === first || active === drawer)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            last?.focus();
          } else if (!event.shiftKey && (active === last || active === drawer)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            first?.focus();
          }
        }, { ...options, capture: true });
      }
      window.addEventListener('resize', () => {
        this.style.setProperty('--tbc-panel-top', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
        if (!this.getClientRects().length) this.closeAll();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const selected = this.querySelector(`[data-shopify-editor-block*='"id":"${event.detail.blockId}"'] details`);
        if (selected) { selected.open = true; prepare(selected); }
      }, options);
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.enterTimer);
      clearTimeout(this.leaveTimer);
    }
  });
}
